/**
 * @file inward-grn-form.tsx
 * @module app/modules/inward/components/inward-grn-form
 * @description Tablet-first "Create GRN" form for UProcure Inward (Samsung Galaxy Tab A11).
 *   The assigned loading bay manager taps "Start inward", then enters driver, vehicle, delivery
 *   time and the received goods (received / missing / damaged per unit). Gate entry number comes
 *   from the assignment; invoice and receipt details are entered later by Finance. Saving is
 *   "Inward done": the GRN waits for Warehouse and Finance approval before stock goes live.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import { Badge, Button, Card, Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import Loader from "../../../../Global/loader";
import { error, success } from "../../../../Global/toast";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import {
  canDoGrnForPo,
  canPerformInwardAction,
  InwardAction,
} from "../utils/inward-access";
import { durationBetween, formatDateTime } from "../utils/inward-timeline";
import {
  buildGrnPayload,
  buildInitialVariants,
  emptyHeader,
  GRN_HEADER_FIELDS,
  GrnHeader,
  GrnVariantState,
  nextBatchNumber,
  nowInEat,
  quantityTypeLabel,
  remainingAfter,
  sumReceived,
  validateGrnForm,
} from "../utils/inward-grn";
import "../styles/inward.css";
import ImageWithFallback from "../../_common/components/image-with-fallback";

interface InwardGrnFormProps {
  /** Inward list route, e.g. "/UProcure/inward" or "/procurement/inward". */
  basePath: string;
}

interface PoSummary {
  refKey: string;
  supplier: string;
}

interface AssignmentSummary {
  loadingBayManager?: { reference?: string; name?: string };
  dock?: { reference?: string; name?: string };
  gateEntryNumber?: string;
  scheduledDate?: string;
  currentSession?: { startedAt?: string; startedBy?: { name?: string } };
}

const InwardGrnForm = ({ basePath }: InwardGrnFormProps) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state?: { poId?: string } };
  const poId = state?.poId;
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canAddBatch = canPerformInwardAction(
    currentUser,
    InwardAction.AddBatch
  );

  const [po, setPo] = useState<PoSummary | null>(null);
  const [header, setHeader] = useState<GrnHeader>(() =>
    emptyHeader(nowInEat())
  );
  const [variants, setVariants] = useState<GrnVariantState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assignment, setAssignment] = useState<AssignmentSummary | null>(null);
  const [starting, setStarting] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const startedAt = assignment?.currentSession?.startedAt;

  // Live "time since inward started" (updates every 30s)
  useEffect(() => {
    if (!startedAt) return;
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, [startedAt]);

  useEffect(() => {
    if (!poId) {
      navigate(basePath, { replace: true });
      return;
    }
    (async () => {
      setLoading(true);
      const response = await new APICallService(
        INWARD.purchaseOrderForGrn,
        poId,
        "",
        "",
        "",
        "",
        PermissionModule.Inward,
        selectedWarehouse?._id
      ).callAPI();
      if (response?.record) {
        setPo({
          refKey: response.record.refKey,
          supplier:
            response.business?.ownerName || response.business?.name || "-",
        });
        setVariants(
          buildInitialVariants(
            response.record.variants ?? [],
            response.batches ?? []
          )
        );
        setAssignment(response.inwardAssignment ?? null);
      }
      setLoading(false);
    })();
  }, [poId, basePath, navigate, selectedWarehouse?._id]);

  const updateType = (
    vIndex: number,
    tIndex: number,
    change: (
      t: GrnVariantState["types"][number]
    ) => GrnVariantState["types"][number]
  ) =>
    setVariants((prev) =>
      prev.map((v, vi) =>
        vi !== vIndex
          ? v
          : {
              ...v,
              types: v.types.map((t, ti) => (ti === tIndex ? change(t) : t)),
            }
      )
    );

  const setRow = (
    vIndex: number,
    tIndex: number,
    rIndex: number,
    field: "stockCount" | "expiry",
    value: string
  ) =>
    updateType(vIndex, tIndex, (t) => ({
      ...t,
      rows: t.rows.map((r, ri) =>
        ri === rIndex ? { ...r, [field]: value } : r
      ),
    }));

  const addBatch = (vIndex: number, tIndex: number) =>
    updateType(vIndex, tIndex, (t) => ({
      ...t,
      rows: [
        ...t.rows,
        { batch: nextBatchNumber(t), stockCount: "", expiry: "" },
      ],
    }));

  const removeBatch = (vIndex: number, tIndex: number, rIndex: number) =>
    updateType(vIndex, tIndex, (t) => ({
      ...t,
      rows: t.rows.filter((_, ri) => ri !== rIndex),
    }));

  const totalReceived = variants.reduce(
    (total, v) => total + v.types.reduce((s, t) => s + sumReceived(t), 0),
    0
  );

  const handleStart = async () => {
    setStarting(true);
    const response = await new APICallService(
      INWARD.startInward,
      {},
      { id: poId, action: "start" },
      "",
      "",
      "",
      PermissionModule.Inward,
      selectedWarehouse?._id
    ).callAPI();
    setStarting(false);
    if (response?.currentSession?.startedAt) {
      setAssignment(response);
      setNow(new Date());
      success("Inward started");
    }
  };

  const handleSubmit = async () => {
    const problem = validateGrnForm(header, variants);
    if (problem) {
      error(problem);
      return;
    }
    setSaving(true);
    const response = await new APICallService(
      INWARD.createGrn,
      buildGrnPayload(header, variants),
      { id: poId, action: "grn" },
      "",
      "",
      "",
      PermissionModule.Inward,
      selectedWarehouse?._id
    ).callAPI();
    setSaving(false);
    if (response) {
      success(
        "Inward done. GRN created and waiting for Warehouse and Finance approval."
      );
      navigate(basePath, { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center p-10">
        <Loader loading={loading} />
      </div>
    );
  }

  if (!po) {
    return (
      <div className="inward-empty" data-testid="inward-grn-not-found">
        Purchase order not found.
      </div>
    );
  }

  return (
    <div className="inward-page inward-grn" data-testid="inward-grn-form">
      <div className="inward-header d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <h1 className="fs-22 fw-bolder mb-1">Create GRN · {po.refKey}</h1>
          <p className="text-muted fs-15 mb-0">Supplier: {po.supplier}</p>
        </div>
        <Button
          variant="light"
          className="inward-touch-btn"
          onClick={() => navigate(basePath)}
        >
          Back
        </Button>
      </div>

      <Card className="inward-card mb-5" data-testid="inward-session">
        <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="fs-15">
            <div>
              Loading bay manager:{" "}
              <strong>
                {assignment?.loadingBayManager?.name || "Not assigned"}
              </strong>
            </div>
            <div>
              Dock: <strong>{assignment?.dock?.name || "-"}</strong>
            </div>
            <div>
              Gate entry number:{" "}
              <strong data-testid="grn-gate-entry">
                {assignment?.gateEntryNumber || "-"}
              </strong>
            </div>
            <div>
              Planned inward date:{" "}
              <strong>{assignment?.scheduledDate || "-"}</strong>
            </div>
          </div>
          {!assignment ? (
            <div
              className="text-danger fw-semibold"
              data-testid="inward-not-assigned"
            >
              Assign a loading bay manager and dock to this PO first.
            </div>
          ) : !canDoGrnForPo(currentUser, assignment) ? (
            <div
              className="text-danger fw-semibold"
              data-testid="inward-not-yours"
            >
              Only{" "}
              {assignment.loadingBayManager?.name ||
                "the assigned loading bay manager"}{" "}
              or a super admin can do this GRN.
            </div>
          ) : startedAt ? (
            <div className="text-end" data-testid="inward-started">
              <Badge
                bg=""
                className="inward-badge inward-badge--info fs-14 py-2 px-3"
              >
                Inward started {formatDateTime(startedAt)}
              </Badge>
              <div className="fs-14 text-muted mt-1">
                In progress for {durationBetween(startedAt, null, now)}
              </div>
            </div>
          ) : (
            <Button
              className="inward-touch-btn px-8"
              disabled={starting}
              onClick={handleStart}
              data-testid="inward-start"
            >
              {starting ? "Starting…" : "Start inward"}
            </Button>
          )}
        </Card.Body>
      </Card>

      {!startedAt ? (
        <div className="inward-empty" data-testid="inward-start-first">
          Tap <strong>Start inward</strong> when the vehicle is at the dock. The
          form opens after that.
        </div>
      ) : (
        <>
          <Card className="inward-card mb-5">
            <Card.Body>
              <h2 className="fs-18 fw-bold mb-4">Delivery details</h2>
              <div className="inward-form-grid">
                {GRN_HEADER_FIELDS.map((field) => (
                  <Form.Group key={field.key} controlId={`grn-${field.key}`}>
                    <Form.Label className="fs-15 fw-semibold">
                      {field.label}
                    </Form.Label>
                    <Form.Control
                      className="inward-input"
                      type={field.type ?? "text"}
                      value={header[field.key]}
                      onChange={(e) =>
                        setHeader((h) => ({
                          ...h,
                          [field.key]: e.target.value,
                        }))
                      }
                      data-testid={`grn-${field.key}`}
                    />
                  </Form.Group>
                ))}
              </div>
            </Card.Body>
          </Card>

          <h2 className="fs-18 fw-bold mb-3">Received goods</h2>
          {variants.length === 0 && (
            <div
              className="inward-empty"
              data-testid="inward-grn-nothing-pending"
            >
              Nothing is pending on this purchase order.
            </div>
          )}
          {variants.map((v, vIndex) => (
            <Card
              key={v.variantId}
              className="inward-card mb-4"
              data-testid="grn-product"
            >
              <Card.Body>
                <div className="d-flex align-items-center gap-3 mb-3">
                  {v.image && (
                    <ImageWithFallback src={v.image} alt="" className="inward-thumb" noDefaultSize />
                  )}
                  <div className="fs-17 fw-bold">{v.title}</div>
                </div>

                {v.types.map((t, tIndex) => (
                  <div
                    key={t.type}
                    className="inward-type-block"
                    data-testid={`grn-type-${t.type}`}
                  >
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                      <span className="fs-16 fw-semibold me-2">
                        {quantityTypeLabel(t.type)}
                      </span>
                      <Badge
                        bg=""
                        className="inward-badge inward-badge--neutral fs-14"
                      >
                        Ordered {t.ordered}
                      </Badge>
                      <Badge
                        bg=""
                        className="inward-badge inward-badge--neutral fs-14"
                      >
                        Received before {t.received}
                      </Badge>
                      <Badge
                        bg=""
                        className="inward-badge inward-badge--primary fs-14"
                      >
                        Pending {t.pending}
                      </Badge>
                    </div>

                    {t.rows.map((row, rIndex) => (
                      <div
                        key={row.batch}
                        className="inward-batch-row"
                        data-testid="grn-batch-row"
                      >
                        <div className="inward-batch-no">Batch {row.batch}</div>
                        <Form.Group>
                          <Form.Label className="fs-14">
                            Quantity received
                          </Form.Label>
                          <Form.Control
                            className="inward-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={t.pending}
                            value={row.stockCount}
                            placeholder="Count and enter"
                            onChange={(e) =>
                              setRow(
                                vIndex,
                                tIndex,
                                rIndex,
                                "stockCount",
                                e.target.value
                              )
                            }
                            data-testid="grn-qty"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label className="fs-14">Expiry date</Form.Label>
                          <Form.Control
                            className="inward-input"
                            type="date"
                            value={row.expiry}
                            onChange={(e) =>
                              setRow(
                                vIndex,
                                tIndex,
                                rIndex,
                                "expiry",
                                e.target.value
                              )
                            }
                            data-testid="grn-expiry"
                          />
                        </Form.Group>
                        {t.rows.length > 1 && (
                          <Button
                            variant="outline-danger"
                            className="inward-touch-btn align-self-end"
                            onClick={() => removeBatch(vIndex, tIndex, rIndex)}
                            data-testid="grn-remove-batch"
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}

                    <div className="d-flex flex-wrap gap-3 align-items-end mt-2">
                      {canAddBatch && (
                        <Button
                          variant="outline-primary"
                          className="inward-touch-btn"
                          onClick={() => addBatch(vIndex, tIndex)}
                          data-testid="grn-add-batch"
                        >
                          + Add batch
                        </Button>
                      )}
                      {(["missing", "damaged"] as const).map((field) => (
                        <Form.Group key={field} className="inward-short-input">
                          <Form.Label className="fs-14">
                            {field === "missing" ? "Missing" : "Damaged"}
                          </Form.Label>
                          <Form.Control
                            className="inward-input"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            value={t[field]}
                            placeholder="0"
                            onChange={(e) =>
                              updateType(vIndex, tIndex, (x) => ({
                                ...x,
                                [field]: e.target.value,
                              }))
                            }
                            data-testid={`grn-${field}`}
                          />
                        </Form.Group>
                      ))}
                      <span className="fs-15" data-testid="grn-type-total">
                        Receiving <strong>{sumReceived(t)}</strong> of{" "}
                        {t.pending}
                        {remainingAfter(t) > 0 && (
                          <>
                            {" · "}
                            <span className="text-warning-emphasis fw-semibold">
                              {remainingAfter(t)} still pending (delayed)
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          ))}
        </>
      )}

      <div className="inward-sticky-footer">
        <span className="fs-16">
          Total receiving:{" "}
          <strong data-testid="grn-total">{totalReceived}</strong>
        </span>
        <Button
          className="inward-touch-btn px-8"
          disabled={saving || variants.length === 0 || !startedAt}
          onClick={handleSubmit}
          data-testid="grn-submit"
        >
          {saving
            ? "Saving…"
            : startedAt
            ? "Save · Inward done"
            : "Start inward first"}
        </Button>
      </div>
    </div>
  );
};

export default InwardGrnForm;
